<script setup lang="ts">
import { useGlobalStore } from '~/stores/global';

const props = defineProps({
  item: {
    type: Object as () => any,
    required: true,
  },
  loadedTopPresets: {
    type: Object,
    required: true,
  },
  loadedBrandPresets: {
    type: Object,
    required: true,
  },
  loadedTopModels: {
    type: Object,
    required: true,
  },
  pageData: {
    type: Object,
    required: true,
  },
  noMoreButton: {
    type: Boolean,
    default: false,
  },
  titleClass: {
    type: String,
    default: 'heading-l',
  },
});

const globalStore = useGlobalStore();
const debugPanel = computed(() => globalStore.debugPanel);

function loadedPresets(id: number, has_children: boolean) {
  if (has_children) return false;
  return !(
    id in props.loadedTopPresets &&
    props.loadedTopPresets[id] &&
    props.loadedTopPresets[id]?.length === 0
  );
}

function getTopPresets(id: number) {
  if (props.loadedTopPresets && id in props.loadedTopPresets) {
    return props.loadedTopPresets[id];
  }
  return undefined;
}

function getTopModels(id: number) {
  if (props.loadedTopModels && id in props.loadedTopModels) {
    return props.loadedTopModels[id];
  }
  return undefined;
}
</script>

<template>
  <li
    v-if="getTopModels(item.id) || loadedTopModels[item.id]"
    class="category-block"
  >
    <nuxt-link
      :to="item.slug ? `/${item.slug}/` : '/'"
      class="category-title"
      :class="titleClass"
    >
      {{ item.name }}
    </nuxt-link>
    <debug-info v-if="debugPanel" class="debug-catalog" :data="item" />
    <div
      v-if="loadedPresets(item.id, item.has_children)"
      class="sub-block-wrap"
    >
      <category-top-presets
        v-if="getTopPresets(item.id) || loadedTopPresets[item.id]"
        :list="getTopPresets(item.id) || loadedTopPresets[item.id]"
      />
      <div v-else class="no-presets" />
    </div>
    <div
      class="sub-block-wrap"
      :data-id="loadedBrandPresets[item.id] ? null : item.id"
      :has_children="item.has_children || null"
    >
      <ModelSimilar
        v-if="getTopModels(item.id) || loadedTopModels[item.id]"
        :similar="getTopModels(item.id) || loadedTopModels[item.id]"
        :model-id="Number(item.id)"
        :changed-page="false"
        :hide-title="true"
        :page-data="pageData"
        full-width-mobile
      />
      <div v-else class="no-similar" />
    </div>
    <CategoryBlocks
      :has-children="item.has_children"
      :categories="
        item.has_children ? item.children : loadedBrandPresets[item.id]
      "
      :link="item.slug"
      :noMoreButton="noMoreButton"
    />
  </li>
</template>

<style scoped lang="scss">
.category-block {
  margin-bottom: 40px;

  &.hidden {
    display: none;
  }

  @include md-block {
    margin-bottom: 48px;
  }

  @include xxl-block {
    margin-bottom: 65px;
  }
}

.category-title {
  display: inline-block;
  text-decoration: none;
  color: $achromatic-black;
  margin-bottom: 12px;

  @include xl-block {
    margin-bottom: 24px;
  }

  &:hover {
    color: $main-cta;
  }
}

.no-presets {
  height: 32px;
  width: 100%;
  @include xl-block {
    height: 44px;
  }
}

.sub-block-wrap {
  margin-bottom: 20px;
  @include md-block {
    margin-bottom: 24px;
  }
  @include xl-block {
    margin-bottom: 28px;
  }
}

.no-similar {
  height: 270px;
  width: 100%;
  @include md-block {
    height: 298px;
  }
  @include xl-block {
    height: 404px;
  }
  @include xxl-block {
    height: 475px;
  }
}
</style>
